import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CalendarCheck, ChevronRight, Dna, Gamepad2, HeartPulse, Home, ListChecks, PlayCircle, ShoppingBag, Sparkles, UserRound, Watch,
} from 'lucide-react';

import { loadMobileView } from './api';
import { Empty, Failed, Head, Loading } from './parts';
import { tx } from './i18n';
import { useLiveResource } from './useLive';
import { isLifecycle, screenName, screenOfStep, sessionVisits, stepLabel, useAppStrings } from './AppPhone';
import SessionPlayer, { ScreenIcon } from './SessionPlayer';
import {
  HeadFacts, HourHeatmap, NUMBER, Panel, PlatformMark, RangeTabs, Share, ago, counted, initials, lengthOf, percent, useNow,
} from './MobileParts';

// What a session was for, from where its time went: the kind of screen it spent
// longest on. A session that never left Home was a quick check.
const JOURNEYS = [
  { key: 'results', Icon: Dna, screens: ['insights', 'results', 'omics', 'reports', 'clinical'] },
  { key: 'health', Icon: HeartPulse, screens: ['metric'] },
  { key: 'logging', Icon: ListChecks, screens: ['diet', 'habits', 'activity'] },
  { key: 'ai', Icon: Sparkles, screens: ['ai'] },
  { key: 'shopping', Icon: ShoppingBag, screens: ['store', 'checkout', 'orders'] },
  { key: 'games', Icon: Gamepad2, screens: ['games'] },
  { key: 'booking', Icon: CalendarCheck, screens: ['booking'] },
  { key: 'devices', Icon: Watch, screens: ['device'] },
  { key: 'account', Icon: UserRound, screens: ['profile', 'tests', 'signin'] },
  { key: 'quick', Icon: Home, screens: [] },
];
const JOURNEY_OF = new Map(JOURNEYS.flatMap((journey) => journey.screens.map((screen) => [screen, journey.key])));
const PATH_LENGTH = 4;

function median(values) {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

// Each session read once: its kind, the first few screens it went through, and where it ended.
function readJourney(session) {
  const visits = sessionVisits(session).filter((visit) => visit.screen !== 'launch');
  const time = new Map();
  visits.forEach((visit, index) => {
    const kind = JOURNEY_OF.get(visit.screen);
    if (kind === undefined) return;
    const next = visits[index + 1];
    time.set(kind, (time.has(kind) ? time.get(kind) : 0) + Math.max(1000, (next ? next.start : visit.end) - visit.start));
  });
  const kind = time.size === 0 ? 'quick' : [...time.entries()].sort((a, b) => b[1] - a[1])[0][0];
  const path = [];
  for (const visit of visits) {
    if (path.length === 0 || path[path.length - 1] !== visit.screen) path.push(visit.screen);
  }
  return { session, kind, path: path.slice(0, PATH_LENGTH), last: visits.length === 0 ? null : visits[visits.length - 1].screen };
}

function pathStepName(screen, as) {
  return screen === 'metric' ? tx('ui.mobile.screen_health_charts') : screenName({ screen, part: null }, as);
}

// How people use the app taken together: the kinds of sessions that keep coming
// back, the usual ways through, and where people put the app down.
function Journeys({ sessions, as, onWatch }) {
  const read = useMemo(() => sessions.map(readJourney), [sessions]);
  const kinds = useMemo(() => JOURNEYS.map((journey) => {
    const matching = read.filter((entry) => entry.kind === journey.key);
    const paths = new Map();
    for (const entry of matching) {
      const key = entry.path.join('>');
      paths.set(key, (paths.has(key) ? paths.get(key) : 0) + 1);
    }
    const usual = [...paths.entries()].sort((a, b) => b[1] - a[1])[0];
    return {
      ...journey,
      sessions: matching.length,
      people: new Set(matching.map((entry) => entry.session.accountUuid || entry.session.id)).size,
      medianMs: median(matching.map((entry) => entry.session.end - entry.session.start)),
      usual: usual ? usual[0].split('>').filter(Boolean) : [],
      example: matching[0] ? matching[0].session.id : null,
    };
  }).filter((journey) => journey.sessions > 0).sort((a, b) => b.sessions - a.sessions), [read]);
  const paths = useMemo(() => {
    const counts = new Map();
    for (const entry of read) {
      if (entry.path.length === 0) continue;
      const key = entry.path.join('>');
      if (!counts.has(key)) counts.set(key, { key, path: entry.path, sessions: 0, people: new Set() });
      counts.get(key).sessions += 1;
      counts.get(key).people.add(entry.session.accountUuid || entry.session.id);
    }
    return [...counts.values()].sort((a, b) => b.sessions - a.sessions || b.path.length - a.path.length).slice(0, 6);
  }, [read]);
  const exits = useMemo(() => {
    const counts = new Map();
    for (const entry of read) if (entry.last !== null) counts.set(entry.last, (counts.has(entry.last) ? counts.get(entry.last) : 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [read]);

  if (read.length === 0) return <Empty title={tx('ui.mobile.no_sessions')} />;
  const total = read.length;
  const mostPath = Math.max(1, ...paths.map((entry) => entry.sessions));
  const mostExit = Math.max(1, ...exits.map(([, count]) => count));
  return (
    <div className="xo-mob-journeys">
      <ol className="xo-mob-journey-kinds">
        {kinds.map((kind) => (
          <li key={kind.key}>
            <span className="xo-mob-journey-head">
              <span className="xo-mob-rank-icon"><kind.Icon aria-hidden="true" /></span>
              <strong>{tx(`ui.mobile.journey_${kind.key}`)}</strong>
              <em>{percent(kind.sessions, total)}</em>
            </span>
            <Share value={kind.sessions} total={total} />
            <small>{counted('ui.mobile.sessions_count', kind.sessions)} · {counted('ui.mobile.people_count', kind.people)} · {tx('ui.mobile.typically', { length: lengthOf(kind.medianMs) })}</small>
            {kind.usual.length > 1 && (
              <span className="xo-mob-path is-small" aria-label={tx('ui.mobile.usual_way')}>
                {kind.usual.map((screen, index) => <span key={`${screen}-${index}`}><ScreenIcon screen={screen} /></span>)}
              </span>
            )}
            {kind.example && (
              <button type="button" className="xo-mob-link" onClick={() => onWatch(kind.example)}>
                <PlayCircle aria-hidden="true" />{tx('ui.mobile.watch_one')}
              </button>
            )}
          </li>
        ))}
      </ol>
      <div className="xo-mob-row is-halves is-inner">
        <div>
          <h3 className="xo-mob-subhead">{tx('ui.mobile.common_paths')}</h3>
          <ol className="xo-mob-paths">
            {paths.map((entry) => (
              <li key={entry.key}>
                <span className="xo-mob-path">
                  {entry.path.map((screen, index) => (
                    <span key={`${screen}-${index}`}><ScreenIcon screen={screen} />{pathStepName(screen, as)}</span>
                  ))}
                </span>
                <Share value={entry.sessions} total={mostPath} />
                <em>{NUMBER.format(entry.sessions)}</em>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <h3 className="xo-mob-subhead">{tx('ui.mobile.where_they_leave')}</h3>
          <ol className="xo-mob-rank is-tight">
            {exits.map(([screen, count]) => (
              <li key={screen}>
                <span className="xo-mob-rank-icon"><ScreenIcon screen={screen} /></span>
                <span className="xo-mob-rank-main"><strong>{pathStepName(screen, as)}</strong><small>{percent(count, total)}</small></span>
                <Share value={count} total={mostExit} />
                <em>{NUMBER.format(count)}</em>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

// Where the time goes, from the sessions the page holds step by step.
function ScreenTime({ sessions, as }) {
  const rows = useMemo(() => {
    const byScreen = new Map();
    for (const session of sessions) {
      const visits = sessionVisits(session);
      visits.forEach((visit, index) => {
        if (visit.screen === 'launch') return;
        const next = visits[index + 1];
        const key = visit.part ? `${visit.screen}:${visit.part}` : visit.screen;
        if (!byScreen.has(key)) byScreen.set(key, { key, visit, visits: 0, ms: 0, people: new Set() });
        const entry = byScreen.get(key);
        entry.visits += 1;
        entry.ms += (next ? next.start : visit.end) - visit.start;
        entry.people.add(session.accountUuid || session.id);
      });
    }
    return [...byScreen.values()].sort((a, b) => b.ms - a.ms || b.visits - a.visits).slice(0, 10);
  }, [sessions]);
  if (rows.length === 0) return <Empty title={tx('ui.mobile.no_sessions')} />;
  const longest = Math.max(1, ...rows.map((row) => row.ms));
  return (
    <ol className="xo-mob-rank">
      {rows.map((row) => (
        <li key={row.key}>
          <span className="xo-mob-rank-icon"><ScreenIcon screen={row.visit.screen} /></span>
          <span className="xo-mob-rank-main"><strong>{screenName(row.visit, as)}</strong><small>{counted('ui.mobile.visits_count', row.visits)} · {counted('ui.mobile.people_count', row.people.size)}</small></span>
          <Share value={row.ms} total={longest} />
          <em>{lengthOf(row.ms)}</em>
        </li>
      ))}
    </ol>
  );
}

// What people did, rather than what loaded: questions to the AI, habits ticked, reports asked for.
function Actions({ steps, as }) {
  const rows = useMemo(() => {
    const byLabel = new Map();
    for (const step of steps) {
      if (isLifecycle(step)) continue;
      const label = stepLabel(step, as);
      if (label === null) continue;
      if (!byLabel.has(label)) byLabel.set(label, { label, screen: screenOfStep(step).screen, count: 0, people: 0 });
      const entry = byLabel.get(label);
      entry.count += step.count;
      entry.people = Math.max(entry.people, step.people);
    }
    return [...byLabel.values()].sort((a, b) => b.count - a.count).slice(0, 10);
  }, [steps, as]);
  if (rows.length === 0) return <Empty title={tx('ui.mobile.no_actions')} />;
  const most = Math.max(1, ...rows.map((row) => row.count));
  return (
    <ol className="xo-mob-rank">
      {rows.map((row) => (
        <li key={row.label}>
          <span className="xo-mob-rank-icon"><ScreenIcon screen={row.screen} /></span>
          <span className="xo-mob-rank-main"><strong>{row.label}</strong><small>{counted('ui.mobile.people_count', row.people)}</small></span>
          <Share value={row.count} total={most} />
          <em>{NUMBER.format(row.count)}</em>
        </li>
      ))}
    </ol>
  );
}

function People({ people, now }) {
  if (people.length === 0) return <Empty title={tx('ui.mobile.no_sessions')} />;
  return (
    <ol className="xo-mob-people">
      {people.slice(0, 8).map((person) => (
        <li key={person.accountUuid}>
          <Link to={`/xomics/mobile/accounts?account=${person.accountUuid}`}>
            <span className="xo-mob-avatar is-small">{initials(person.name, person.email)}</span>
            <span className="xo-mob-people-main"><strong>{person.name || person.email}</strong><small><PlatformMark platform={person.platform} />{ago(person.lastAt, now)}</small></span>
            <span className="xo-mob-people-count"><strong>{NUMBER.format(person.sessions)}</strong><small>{lengthOf(person.totalMs)}</small></span>
            <ChevronRight aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ol>
  );
}

function busiestHour(grid) {
  const hours = new Array(24).fill(0);
  for (const row of grid) row.forEach((value, hour) => { hours[hour] += value; });
  const peak = Math.max(...hours);
  return peak === 0 ? null : hours.indexOf(peak);
}

export default function MobileUsage() {
  const [days, setDays] = useState(30);
  const [searchParams, setSearchParams] = useSearchParams();
  const load = useCallback(() => loadMobileView('analytics', days), [days]);
  const report = useLiveResource([], load, { reconcileMs: 120000 });
  const now = useNow(30000);
  const as = useAppStrings();
  const player = useRef(null);
  const picked = searchParams.get('session');
  const watch = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    next.set('session', id);
    setSearchParams(next, { replace: true });
    if (player.current) player.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [searchParams, setSearchParams]);

  if (report.loading && report.data === null) return <Loading label={tx('ui.mobile.reading_usage')} />;
  if (report.error && report.data === null) return <Failed message={report.error} onRetry={report.refresh} />;
  const data = report.data;
  const peak = busiestHour(data.heatmap);

  return (
    <div className="xo-platform-page xo-mobile-page">
      <Head title={tx('ui.mobile.usage_title')} subtitle={tx('ui.mobile.usage_subtitle')}>
        <RangeTabs value={days} onChange={setDays} />
      </Head>
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}

      <div ref={player} className="xo-mob-anchor">
        <Panel title={tx('ui.mobile.sessions_title')} detail={tx('ui.mobile.sessions_detail', { count: NUMBER.format(data.sessions.length) })} className="is-player"
          aside={<HeadFacts items={[
            { label: tx('ui.mobile.sessions'), value: NUMBER.format(data.totals.sessions) },
            { label: tx('ui.mobile.people'), value: NUMBER.format(data.totals.people) },
            { label: tx('ui.mobile.typical_session_label'), value: lengthOf(data.totals.medianMs) },
          ]} />}>
          <SessionPlayer key={`${days}-${picked}`} sessions={data.sessions} initialId={picked} now={now} />
        </Panel>
      </div>

      <Panel title={tx('ui.mobile.journeys_title')} detail={tx('ui.mobile.journeys_detail', { count: NUMBER.format(data.sessions.length) })}>
        <Journeys sessions={data.sessions} as={as} onWatch={watch} />
      </Panel>

      <div className="xo-mob-row is-halves">
        <Panel title={tx('ui.mobile.screen_time_title')} detail={tx('ui.mobile.screen_time_detail', { count: NUMBER.format(data.sessions.length) })}>
          <ScreenTime sessions={data.sessions} as={as} />
        </Panel>
        <Panel title={tx('ui.mobile.actions_title')} detail={tx('ui.mobile.actions_detail')}>
          <Actions steps={data.steps} as={as} />
        </Panel>
      </div>

      <div className="xo-mob-row is-wide-left">
        <Panel title={tx('ui.mobile.opens_title')} detail={tx('ui.mobile.opens_detail')}
          aside={peak !== null && <HeadFacts items={[{ label: tx('ui.mobile.busiest_hour'), value: `${String(peak).padStart(2, '0')}:00` }]} />}>
          <HourHeatmap grid={data.heatmap} countKey="ui.mobile.sessions_count" />
        </Panel>
        <Panel title={tx('ui.mobile.leaders_title')} detail={tx('ui.mobile.leaders_detail')}>
          <People people={data.people} now={now} />
        </Panel>
      </div>
    </div>
  );
}
